import React, { useMemo, useState } from "react";
import { formatDate } from "../../../../../../utilities/formatDate";
import localeText from "../../../../../../locale";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { AdvancedImage } from "@cloudinary/react";
import { DataTable } from "primereact/datatable";
import { FilterMatchMode } from "primereact/api";
import { fill } from "@cloudinary/url-gen/actions/resize";
import { cloudinaryInstance } from "../../../../../../utilities/Cloudinary/cloudinaryInstance";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { useHistory, useLocation } from "react-router-dom";
import UserInitialsAvatar from "../../../../../../components/UserInitialsAvatar/UserInitialsAvatar";
import "./OrdersTable.css";

const STATUS_FILTER_ALL = "all";
const STATUS_FILTER_PAID = "PAID";
const STATUS_FILTER_SENT = "SENT";
const STATUS_FILTER_RECEIVED = "RECEIVED";

const OrdersTable = ({
  admin,
  orders,
  loading,
  sales,
  setCurrency,
  handleCurrency,
  setCurrentOrder,
  setOrderId_,
  setDisplayRateVisible,
}) => {
  const filters2 = {
    id: { value: null, matchMode: FilterMatchMode.CONTAINS },
    "buyer.username": { value: null, matchMode: FilterMatchMode.STARTS_WITH },
    "seller.username": { value: null, matchMode: FilterMatchMode.STARTS_WITH },
    status: { value: null, matchMode: FilterMatchMode.EQUALS },
    formatedDate: { value: null, matchMode: FilterMatchMode.EQUALS },
  };
  const history = useHistory();
  const url = useLocation();
  const urlToGo = url?.pathname?.includes("sales")
    ? "/dashboard-salesdetails?orderId="
    : "/dashboard-purchasesdetails?orderId=";

  const [statusFilter, setStatusFilter] = useState(STATUS_FILTER_ALL);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredOrders = useMemo(() => {
    if (admin) return orders || [];
    const list = Array.isArray(orders) ? orders : [];
    let result = list;
    if (statusFilter !== STATUS_FILTER_ALL) {
      result = result.filter((o) => o.status === statusFilter);
    }
    const q = (searchQuery || "").trim().toLowerCase();
    if (q) {
      result = result.filter((o) => {
        const idStr = String(o.id ?? "").toLowerCase();
        const seller = (o.seller?.username ?? "").toLowerCase();
        const buyer = (o.buyer?.username ?? "").toLowerCase();
        return idStr.includes(q) || seller.includes(q) || buyer.includes(q);
      });
    }
    return result;
  }, [admin, orders, statusFilter, searchQuery]);

  const adminOrders = useMemo(() => {
    const list = Array.isArray(orders) ? orders : [];
    return list
      .map((order) => ({
        ...order,
        dateValue: order.date ? new Date(order.date).getTime() : 0,
      }))
      .sort((a, b) => b.dateValue - a.dateValue);
  }, [orders]);

  const renderEmptyMessage = () => (
    <div className="flex flex-column align-items-center justify-content-center py-8 gap-3">
      <div
        className="border-round flex align-items-center justify-content-center"
        style={{
          width: "120px",
          height: "120px",
          backgroundColor: "color-mix(in srgb, var(--theme-border) 35%, transparent)",
        }}
      >
        <i className="pi pi-inbox text-6xl text-400" />
      </div>
      <p className="m-0" style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
        {localeText("noOperationsRecords")}
      </p>
    </div>
  );

  const detailPath = (order) => `${urlToGo}${order.id}`;

  const renderPreviewBody = (order) => (
    <div className="flex">
      {(order.itens || [])
        .filter((_, index) => index < 7)
        .map((item) => (
          <div key={`p${item.id}`}>
            <AdvancedImage
              cldImg={cloudinaryInstance
                .image(
                  item.productVariation ? item.productVariation.imageid : item.product?.imageid1,
                )
                .resize(fill().width(50).height(50))
                .format("webp")}
              style={{ outline: "1px solid var(--theme-border, #e0e0e0)", outlineOffset: "4px" }}
              className="border-round mr-3"
              height={50}
            />
          </div>
        ))}
    </div>
  );

  return (
    <>
      {!admin ? (
        <>
          <div className="flex flex-wrap align-items-center gap-2 mb-3">
            <div
              className="flex gap-1 flex-wrap p-1"
              style={{
                backgroundColor: "color-mix(in srgb, var(--theme-border) 35%, transparent)",
                borderRadius: "0.625rem",
              }}
            >
              <Button
                size="small"
                label={localeText("all") || "Todos"}
                className={`p-button-text border-round-pill font-medium ${statusFilter === STATUS_FILTER_ALL ? "filter-pill-active" : ""} p-no-outline`}
                style={{ color: "#A3A3A3" }}
                onClick={() => setStatusFilter(STATUS_FILTER_ALL)}
              />
              <Button
                size="small"
                label={localeText("paid") || "Pago"}
                className={`p-button-text border-round-pill font-medium ${statusFilter === STATUS_FILTER_PAID ? "filter-pill-active" : ""} p-no-outline`}
                style={{ color: "#A3A3A3" }}
                onClick={() => setStatusFilter(STATUS_FILTER_PAID)}
              />
              <Button
                size="small"
                label={localeText("sent") || "Enviado"}
                className={`p-button-text border-round-pill font-medium ${statusFilter === STATUS_FILTER_SENT ? "filter-pill-active" : ""} p-no-outline`}
                style={{ color: "#A3A3A3" }}
                onClick={() => setStatusFilter(STATUS_FILTER_SENT)}
              />
              <Button
                size="small"
                label={localeText("received") || "Recebidos"}
                className={`p-button-text border-round-pill font-medium ${statusFilter === STATUS_FILTER_RECEIVED ? "filter-pill-active" : ""} p-no-outline`}
                style={{ color: "#A3A3A3" }}
                onClick={() => setStatusFilter(STATUS_FILTER_RECEIVED)}
              />
            </div>
            <InputText
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={localeText("searchByIdOrSeller") || "Busca pelo ID ou vendedor"}
              className="p-inputtext-sm border-round-md p-no-outline"
              style={{ minWidth: "14rem" }}
            />
          </div>
          <DataTable
            value={filteredOrders}
            paginator
            responsiveLayout="scroll"
            loading={loading}
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
            currentPageReportTemplate=""
            rows={10}
            rowsPerPageOptions={[10, 20, 50]}
            emptyMessage={renderEmptyMessage()}
            onRowClick={(e) => history.push(detailPath(e.data))}
            // PrimeReact 10 passa [rowData, options] como array para o rowClassName
            rowClassName={(args) =>
              !sales && (Array.isArray(args) ? args[0] : args)?.status === "SENT"
                ? "cursor-pointer order-row--awaiting-receipt"
                : "cursor-pointer"
            }
            className="financial-transactions-datatable"
          >
            <Column
              header={localeText("preview")}
              body={renderPreviewBody}
              style={{ width: "20%" }}
            />
            <Column field="id" header="ID" style={{ width: "12%" }} sortable />
            <Column
              field={sales ? "buyer.username" : "seller.username"}
              header={sales ? localeText("byBuyer") : localeText("seller")}
              style={{ width: "18%" }}
              sortable
            />
            <Column
              field="itens.length"
              header={localeText("itemsQuantity")}
              style={{ width: "10%" }}
            />
            <Column
              header={localeText("status")}
              body={(order) => {
                if (order.status === "SENT") {
                  return (
                    <span
                      className="px-2 py-1 text-xs border-round"
                      style={{ backgroundColor: "#42A5F5", color: "#fff" }}
                    >
                      {localeText("sent").toUpperCase()}
                    </span>
                  );
                }
                if (order.status === "PAID") {
                  return (
                    <span
                      className="px-2 py-1 text-xs border-round"
                      style={{ backgroundColor: "#F7B005", color: "#fff" }}
                    >
                      {localeText("paid").toUpperCase()}
                    </span>
                  );
                }
                if (order.status === "RECEIVED") {
                  return (
                    <span
                      className="px-2 py-1 text-xs border-round"
                      style={{ backgroundColor: "#46a748", color: "#fff" }}
                    >
                      {localeText("received").toUpperCase()}
                    </span>
                  );
                }
                return null;
              }}
              sortable
              field="status"
              style={{ width: "14%" }}
            />
            <Column
              header={localeText("date")}
              body={(order) => formatDate(new Date(order.date))}
              sortable
              field="date"
              style={{ width: "14%" }}
            />
            <Column
              header={localeText("rating")}
              body={(order) =>
                order.status === "RECEIVED" && !order.evaluated && !sales ? (
                  <Button
                    label={localeText("rate")}
                    icon="pi pi-star"
                    className="p-button-warning"
                    onClick={(e) => {
                      e.stopPropagation();
                      history.push(`/dashboard-purchases-evaluate?orderId=${order.id}`);
                    }}
                  />
                ) : null
              }
              style={{ width: "12%" }}
            />
          </DataTable>
        </>
      ) : (
        <DataTable
          value={adminOrders}
          paginator
          responsiveLayout="scroll"
          loading={loading}
          filters={filters2}
          filterDisplay="row"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
          currentPageReportTemplate=""
          rows={10}
          rowsPerPageOptions={[10, 20, 50]}
          emptyMessage={renderEmptyMessage()}
          sortField="dateValue"
          sortOrder={-1}
          // PrimeReact 10 passa [rowData, options] como array para o rowClassName
          rowClassName={(args) =>
            (Array.isArray(args) ? args[0] : args)?.status === "PAID" ? "order-row--pending-action" : ""
          }
          className="financial-transactions-datatable"
        >
          <Column
            header={localeText("buyerProfilePic")}
            style={{ width: "10%" }}
            body={(order) => {
              return (
                <>
                  <UserInitialsAvatar
                    avatarUrl={order?.buyer?.urlAvatar}
                    firstName={order?.buyer?.firstname}
                    lastName={order?.buyer?.surname}
                    fullName={order?.buyer?.username}
                    className="layout-dashboard-urlavatar"
                    alt="buyer-avatar"
                  />
                </>
              );
            }}
          />
          <Column
            header={localeText("sellerProfilePic")}
            style={{ width: "10%" }}
            body={(order) => {
              return (
                <>
                  <UserInitialsAvatar
                    avatarUrl={order?.seller?.urlAvatar}
                    firstName={order?.seller?.firstname}
                    lastName={order?.seller?.surname}
                    fullName={order?.seller?.username}
                    className="layout-dashboard-urlavatar"
                    alt="seller-avatar"
                  />
                </>
              );
            }}
          />
          <Column
            field="id"
            header={"ID"}
            style={{ width: "20%" }}
            filter
            filterPlaceholder={localeText("search")}
            filters={filters2}
            filterDisplay="row"
            sortable
          />
          <Column
            field="buyer.username"
            style={{ width: "20%" }}
            header={localeText("byBuyer")}
            sortable
            filter
            filterPlaceholder={localeText("searchByBuyer")}
            filters={filters2}
            filterDisplay="row"
          />
          <Column
            field="seller.username"
            style={{ width: "20%" }}
            header={localeText("seller")}
            sortable
            filter
            filterPlaceholder={localeText("searchBySeller")}
            filters={filters2}
            filterDisplay="row"
          />
          <Column
            header={localeText("preview")}
            body={(order) => {
              return (
                <div className="flex">
                  {order.itens
                    .filter((order, index) => index < 7)
                    .map((order) => {
                      return (
                        <div key={`p${order.id}`}>
                          <AdvancedImage
                            cldImg={cloudinaryInstance
                              .image(
                                order.productVariation
                                  ? order.productVariation.imageid
                                  : order.product.imageid1,
                              )
                              .resize(fill().width(50).height(50))
                              .format("webp")}
                            style={{ outline: "1px solid #e0e0e0", outlineOffset: "4px" }}
                            className="border-round mr-3"
                            height={50}
                          />
                        </div>
                      );
                    })}
                </div>
              );
            }}
            style={{ width: "30%" }}
          />
          <Column
            field="itens.length"
            header={localeText("itemsQuantity")}
            style={{ width: "10%" }}
          />
          <Column
            header={localeText("orderDetails")}
            body={(order) => {
              return (
                <Button
                  icon="pi pi-eye"
                  onClick={() => {
                    const path = `${urlToGo}${order.id}`;
                    history.push(path);
                    // setCurrency(handleCurrency(order.itens[0].product.currency));
                    // setCurrentOrder(order);
                  }}
                  className="p-button-help p-button-rounded"
                  style={{ textSize: "4em" }}
                />
              );
            }}
            style={{ width: "10%" }}
          />
          <Column
            header={localeText("status")}
            style={{ minWidth: "150px" }}
            body={(order) => {
              return (
                <>
                  {order.status === "SENT" && (
                    <span className="border-round px-2 py-1" style={{ backgroundColor: "#42A5F5" }}>
                      {localeText("sent").toUpperCase()}
                    </span>
                  )}
                  {order.status === "PAID" && (
                    <span className="border-round px-2 py-1" style={{ backgroundColor: "#F7B005" }}>
                      {localeText("paid").toUpperCase()}
                    </span>
                  )}
                  {order.status === "PENDING" && (
                    <span className="border-round px-2 py-1" style={{ backgroundColor: "#F7B005" }}>
                      {localeText("pending").toUpperCase()}
                    </span>
                  )}
                  {order.status === "RECEIVED" && (
                    <span className="border-round px-2 py-1" style={{ backgroundColor: "#46a748" }}>
                      {localeText("received").toUpperCase()}
                    </span>
                  )}
                </>
              );
            }}
            filter
            showFilterMenu={false}
            filterElement={(option) => (
              <Dropdown
                value={option.value}
                options={[
                  { label: localeText("sent"), value: "SENT" },
                  { label: localeText("paid"), value: "PAID" },
                  { label: localeText("pending"), value: "PENDING" },
                  { label: localeText("received"), value: "RECEIVED" },
                ]}
                onChange={(e) => option.filterApplyCallback(e.value)}
                placeholder={localeText("status")}
              />
            )}
            filters={filters2}
            filterDisplay="row"
            sortable
            field="status"
          ></Column>
          <Column
            header={localeText("date")}
            body={(order) => {
              return <p>{order.formatedDate}</p>;
            }}
            field="formatedDate"
            sortField="dateValue"
            style={{ minWidth: "180px" }}
            filters={filters2}
            showFilterMenu={false}
            filter
            filterElement={(option) => {
              return (
                <Calendar
                  value={option.value}
                  onChange={(e) => {
                    if (e.value !== null && e.value !== undefined) {
                      option.filterApplyCallback(formatDate(e.value));
                    }
                  }}
                  dateFormat="dd/mm/yy"
                />
              );
            }}
            sortable
            filterDisplay="row"
          />
        </DataTable>
      )}
    </>
  );
};

export default OrdersTable;
